import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, CheckCircle2, Loader2, MailCheck, Megaphone, BellRing, KeyRound } from "lucide-react";
import { Navbar } from "@/components/marketing/Navbar";
import { Footer } from "@/components/marketing/Footer";
import { useSiteContent } from "@/hooks/useSiteContent";
import { SectionLabel } from "@/components/marketing/SectionLabel";
import { PillButton } from "@/components/marketing/PillButton";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SmsTermsBlock, SMS_MARKETING_CONSENT_TEXT, TWOFA_CONSENT_TEXT } from "@/components/marketing/SmsTerms";

export const ConsentRow = ({ checked, onToggle, text, testid }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
    data-testid={testid}
    data-state={checked ? "checked" : "unchecked"}
    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${checked ? "border-oath-accent bg-oath-accent-light" : "border-gray-200 bg-white hover:border-gray-300"}`}
  >
    <Tick checked={checked} />
    <span className="text-xs leading-relaxed text-oath-secondary">{text}</span>
  </div>
);

export const Tick = ({ checked }) => (
  <span
    aria-hidden="true"
    data-state={checked ? "checked" : "unchecked"}
    className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-sm border ${checked ? "border-oath-ink bg-oath-ink text-white" : "border-gray-400 bg-white"}`}
  >
    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
  </span>
);

export const CATEGORIES = [
  {
    id: "promotional",
    icon: Megaphone,
    title: "Promotional offers",
    desc: "Occasional offers, discounts, and early-access invitations for ResidenceProof services.",
  },
  {
    id: "marketing",
    icon: MailCheck,
    title: "Mixed marketing campaigns",
    desc: "Newsletters, product announcements, webinars, industry research, and event invitations.",
  },
  {
    id: "account_updates",
    icon: BellRing,
    title: "Account updates",
    desc: "Service notices, case status changes, billing reminders, and important policy updates.",
  },
];

export const CONSENT_TEXT =
  "I agree to receive the communications I have selected above from ResidenceProof at the email address I provided. I can unsubscribe at any time using the unsubscribe link in any email or by contacting support@residenceproof.com. I have read the Privacy Policy and Messaging Terms.";

export const TwoFactorStep = ({ optIn, support, onDone, embedded, stepLabel = "STEP 2 OF 2" }) => {
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const save = async (consent) => {
    setLoading(true);
    try {
      await api.post(`/opt-in/${optIn.id}/two-factor`, { step_token: optIn.step_token, consent, consent_text: consent ? TWOFA_CONSENT_TEXT : "" });
      onDone(consent);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={`rounded-3xl border border-gray-200 bg-white ${embedded ? "p-5 sm:p-6" : "mt-10 p-6 sm:p-8"}`} data-testid="optin-2fa-step">
      <div className="font-mono-label text-[10px] font-semibold text-oath-accent">{stepLabel}</div>
      <div className="mt-3 flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-oath-ink text-white"><KeyRound className="h-5 w-5" /></span>
        <div>
          <h2 className="font-display text-2xl font-bold text-oath-ink">Two-factor verification codes by text</h2>
          <p className="mt-2 text-sm text-oath-secondary">
            Optionally, we can text a one-time code to <b data-testid="optin-2fa-phone">{optIn.phone}</b> when you sign in or confirm a sensitive action. This is separate from marketing and is never used for promotions.
          </p>
        </div>
      </div>
      <div className="mt-6"><ConsentRow checked={agree} onToggle={() => setAgree((v) => !v)} text={TWOFA_CONSENT_TEXT} testid="optin-2fa-consent" /></div>
      <div className="mt-4"><SmsTermsBlock support={support} testid="optin-2fa-terms" /></div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" disabled={loading || !agree} onClick={() => save(true)} data-testid="optin-2fa-submit"
          className="inline-flex items-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white hover:bg-black disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "AGREE & FINISH"}
        </button>
        <button type="button" disabled={loading} onClick={() => save(false)} data-testid="optin-2fa-skip" className="text-sm text-oath-secondary underline">
          No thanks, skip
        </button>
      </div>
    </div>
  );
};

const Shell = ({ embedded, width = "max-w-3xl", children }) =>
  embedded ? (
    <div id="optin-flow" className="scroll-mt-28">{children}</div>
  ) : (
    <div className="min-h-screen bg-oath-bg">
      <Navbar />
      <main className={`mx-auto ${width} px-5 pb-24 pt-32`}>{children}</main>
      <Footer />
    </div>
  );

export default function CommsOptIn() {
  return <OptInFlow />;
}

export const OptInFlow = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { contact } = useSiteContent();
  const [submitted, setSubmitted] = useState(false);
  const [optIn, setOptIn] = useState(null);
  const [twofa, setTwofa] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", website: "" });
  const [cats, setCats] = useState({ promotional: false, marketing: false, account_updates: false });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!embedded) {
      window.scrollTo({ top: 0, behavior: "instant" });
    } else if (optIn || submitted) {
      document.getElementById("optin-flow")?.scrollIntoView({ block: "start", behavior: "instant" });
    }
  }, [optIn, submitted, embedded]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCat = (id) => setCats((c) => ({ ...c, [id]: !c[id] }));

  const submit = async (e) => {
    e.preventDefault();
    const selected = Object.keys(cats).filter((k) => cats[k]);
    if (selected.length === 0) {
      toast.error("Please select at least one type of communication.");
      return;
    }
    if (!consent) {
      toast.error("Please check the consent box to opt in.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/opt-in", {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        categories: selected,
        consent: true,
        consent_text: CONSENT_TEXT,
        sms_marketing_consent: smsConsent,
        sms_consent_text: smsConsent ? SMS_MARKETING_CONSENT_TEXT : "",
        website: form.website,
      });
      setOptIn(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted && optIn) {
    return (
      <Shell embedded={embedded} width="max-w-2xl">
          <div className={`rounded-3xl border border-gray-200 bg-white text-center ${embedded ? "p-7" : "mt-4 p-10"}`} data-testid="optin-success">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">
              You're subscribed.
            </h1>
            <p className="mt-4 text-oath-secondary">
              Thanks — your communication preferences have been recorded. You can
              change them anytime by replying STOP to texts, using the unsubscribe
              link in any email, or contacting {contact.support_email}.
            </p>
            <ul className="mx-auto mt-5 grid max-w-sm gap-1 text-left text-sm text-oath-secondary" data-testid="optin-summary">
              <li>Marketing &amp; promotional texts: <b>{optIn?.sms_marketing_consent ? "Yes" : "No"}</b></li>
              <li>Two-factor codes by text: <b>{twofa ? "Yes" : "No"}</b></li>
            </ul>
            {!embedded && (
              <div className="mt-8 flex justify-center">
                <PillButton onClick={() => navigate("/")} variant="dark">BACK TO HOME</PillButton>
              </div>
            )}
          </div>
      </Shell>
    );
  }

  if (optIn) {
    return (
      <Shell embedded={embedded}>
          <TwoFactorStep optIn={optIn} support={contact.support_email} embedded={embedded} onDone={(v) => { setTwofa(v); setSubmitted(true); }} />
      </Shell>
    );
  }

  return (
    <Shell embedded={embedded}>
        {embedded ? (
          <header>
            <SectionLabel number="00">COMMUNICATION PREFERENCES · STEP 1 OF 2</SectionLabel>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-oath-ink">Text &amp; email opt-in</h2>
            <p className="mt-2 text-sm text-oath-secondary">Choose which messages you'd like from ResidenceProof. Signing in does not require opting in.</p>
          </header>
        ) : (
        <header className="animate-fade-up">
          <SectionLabel number="00">COMMUNICATION PREFERENCES · STEP 1 OF 2</SectionLabel>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-oath-ink sm:text-5xl">
            Stay in the loop
          </h1>
          <p className="mt-4 max-w-xl text-oath-secondary">
            Choose which messages you'd like to receive from ResidenceProof. Select
            any combination — promotional offers, mixed marketing campaigns, and
            account updates — then confirm your consent below.
          </p>
        </header>
        )}

        <form onSubmit={submit} data-testid="optin-form" className={`rounded-3xl border border-gray-200 bg-white ${embedded ? "mt-5 p-5 sm:p-6" : "mt-10 p-6 sm:p-8"}`}>
          {/* honeypot */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
            aria-hidden="true"
          />

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="flex flex-col gap-2 sm:col-span-1">
              <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">FULL NAME *</Label>
              <Input data-testid="optin-name" value={form.full_name} onChange={set("full_name")} required placeholder="Jordan Avery" />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-1">
              <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">EMAIL *</Label>
              <Input data-testid="optin-email" type="email" value={form.email} onChange={set("email")} required placeholder="jordan@company.com" />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-1">
              <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">MOBILE PHONE *</Label>
              <Input data-testid="optin-phone" value={form.phone} onChange={set("phone")} required placeholder="+1 555 000 0000" />
            </div>
          </div>

          <div className="mt-7">
            <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">
              I WOULD LIKE TO RECEIVE *
            </Label>
            <div className="mt-3 grid gap-3">
              {CATEGORIES.map((c) => (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCat(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCat(c.id); } }}
                  data-testid={`optin-cat-${c.id}`}
                  data-state={cats[c.id] ? "checked" : "unchecked"}
                  className={`flex cursor-pointer items-start gap-4 rounded-2xl border p-4 transition-colors ${
                    cats[c.id] ? "border-oath-accent bg-oath-accent-light" : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <Tick checked={cats[c.id]} />
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-gray-200 bg-white text-oath-accent">
                    <c.icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <span>
                    <span className="block font-display font-bold text-oath-ink">{c.title}</span>
                    <span className="mt-1 block text-sm text-oath-secondary">{c.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-7 rounded-2xl border border-gray-200 bg-oath-bg/60 p-4">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setConsent((v) => !v)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setConsent((v) => !v); } }}
              data-testid="optin-consent"
              data-state={consent ? "checked" : "unchecked"}
              className="flex cursor-pointer items-start gap-3"
            >
              <Tick checked={consent} />
              <span className="text-xs leading-relaxed text-oath-secondary">{CONSENT_TEXT}</span>
            </div>
          </div>

          <div className="mt-5">
            <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">PROMOTIONAL &amp; MARKETING TEXT MESSAGES (OPTIONAL)</Label>
            <div className="mt-3"><ConsentRow checked={smsConsent} onToggle={() => setSmsConsent((v) => !v)} text={SMS_MARKETING_CONSENT_TEXT} testid="optin-sms-consent" /></div>
          </div>

          <div className="mt-4"><SmsTermsBlock support={contact.support_email} /></div>

          <p className="mt-4 text-xs text-oath-muted">
            Next, you can choose whether to also receive two-factor verification codes by text.{" "}
            <Link to="/privacy" className="text-oath-accent underline">Privacy Policy</Link> ·{" "}
            <Link to="/messaging-terms" className="text-oath-accent underline">Messaging Terms</Link>
          </p>

          <div className="mt-6">
            <button
              type="submit"
              disabled={loading}
              data-testid="optin-submit"
              className="group inline-flex items-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "CONTINUE"}
              {!loading && <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">→</span>}
            </button>
          </div>
        </form>
    </Shell>
  );
};
